import { useMemo } from 'react'

import { TableCellTags } from '@/feature/tags'

import { Loader } from '@/lib/core/components/Loader'

import { Asset } from '@/lib/features/Assets/interfaces'

import styles from './AssetPanel.module.css'
import { PANEL_MAX_VISIBLE_TAGS } from './AssetTagsTab.utils'
import { useAssetTags } from './useAssetTags'

interface AssetTagsTabProps {
  asset: Asset
}

/**
 * Tags assigned to the selected asset, with the same tag icon affordance used in the assets table:
 * clicking it opens the shared tags popout to assign or unassign tags without leaving the map.
 *
 * The popout itself is mounted by `AssetPanel` outside the drawer — the drawer is `transform`ed,
 * which would make it the containing block for the popout's `position: fixed` and throw off the
 * viewport coordinates it positions itself with.
 */
export const AssetTagsTab = ({ asset }: AssetTagsTabProps) => {
  const { tags, isLoading } = useAssetTags(asset)

  // The popout edits whatever asset it is handed, so give it the resolved tags rather than the raw
  // ones. This also guarantees `tags` is an array — `useTagToggle` bails out when it is undefined,
  // which would otherwise block assigning the first tag to an asset that has none.
  const assetData = useMemo(() => ({ ...asset, tags }), [asset, tags])

  if (isLoading) {
    return (
      <div className={styles.tagsList} data-testid="asset-panel-tags-tab">
        <Loader size="2rem" />
      </div>
    )
  }

  return (
    <div className={styles.tagsList} data-testid="asset-panel-tags-tab">
      <TableCellTags assetData={assetData} maxTagsRenderCount={PANEL_MAX_VISIBLE_TAGS} />
      {tags.length === 0 && <span className={styles.detailValue}>No tags assigned</span>}
    </div>
  )
}
